import { Chip, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { toDate } from "helpers/dateHelper";
import { useRemoteResource } from "hooks/useRemoteResource";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ticketsService from "services/admin/ticketsService";
import type { Ticket } from "interfaces/entities/ticket.model";

type ChipColor = "default" | "info" | "warning" | "success" | "error" | "primary" | "secondary";
type ChipVariant = "filled" | "outlined";

const STATUS_STYLE: Record<string, { color: ChipColor; variant: ChipVariant }> = {
    NEW: { color: "info", variant: "filled" },
    IN_PROGRESS: { color: "warning", variant: "filled" },
    WAITING_FOR_RESPONSE: { color: "warning", variant: "outlined" },
    RESOLVED: { color: "success", variant: "filled" },
    CLOSED: { color: "default", variant: "outlined" },
};

const PRIORITY_STYLE: Record<string, ChipColor> = {
    HIGH: "error",
    MEDIUM: "warning",
    LOW: "info",
};

interface CustomerTicketsProps {
    customerID: string | number;
}

export const CustomerTickets = ({ customerID }: CustomerTicketsProps) => {
    const { t } = useTranslation();
    const [data, , loading] = useRemoteResource(() =>
        ticketsService.getAllAndCount({ customerID, limit: 100 })
    );

    const tickets: Ticket[] = (data as any)?.rows || [];
    const count: number = (data as any)?.count || 0;

    function buildContent() {
        if (loading) {
            return <div className="text-center"><CircularProgress /></div>;
        }
        if (!tickets.length) {
            return <div>Обращения отсутствуют у клиента</div>;
        }
        return (
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Тема</TableCell>
                            <TableCell>Статус</TableCell>
                            <TableCell>Приоритет</TableCell>
                            <TableCell>Тип</TableCell>
                            <TableCell>Тематика</TableCell>
                            <TableCell>Создано</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tickets.map((ticket) => {
                            const statusStyle = STATUS_STYLE[ticket.status] || { color: "default" as ChipColor, variant: "filled" as ChipVariant };
                            return (
                                <TableRow key={ticket.id} hover>
                                    <TableCell>
                                        <Link to={`/admin/tickets/${ticket.id}`}>#{ticket.id}</Link>
                                    </TableCell>
                                    <TableCell>
                                        <Link to={`/admin/tickets/${ticket.id}`}>{ticket.subject || 'Без темы'}</Link>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small"
                                            label={t(`ticket.status.${ticket.status}`)}
                                            color={statusStyle.color}
                                            variant={statusStyle.variant}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {ticket.priority ? (
                                            <Chip
                                                size="small"
                                                label={t(`priority.${ticket.priority}`)}
                                                color={PRIORITY_STYLE[ticket.priority] || "default"}
                                                variant="outlined"
                                            />
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell>{ticket.type ? t(`ticket.type.${ticket.type}`) : '—'}</TableCell>
                                    <TableCell>{(ticket as any).category?.name || '—'}</TableCell>
                                    <TableCell>{toDate(ticket.createdAt)}</TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    return (
        <div className="m-t-32">
            <h3 className="m-b-16">Обращения {count ? `(${count})` : ''}</h3>
            {buildContent()}
        </div>
    );
};

export default CustomerTickets;
